import type { CSSProperties, KeyboardEvent, ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "../utils/cx.js";

export interface TableColumn<T> {
  key: string;
  title: ReactNode;
  render?: (row: T) => ReactNode;
  /** Right-align + tabular figures (for amounts). */
  numeric?: boolean;
  /** Fixed column width (px number or CSS length) to balance the layout. */
  width?: number | string;
  /** Hide below the `sm` breakpoint — for columns that are context, not identity. */
  hideOnMobile?: boolean;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  rowKey: (row: T) => string;
  empty?: ReactNode;
  /** Makes rows interactive (pointer cursor) — e.g. navigate to a detail page. */
  onRowClick?: (row: T) => void;
  /** Row density. Defaults to "small" — the app standard is compact tables. */
  size?: "small" | "middle" | "large";
  /**
   * Renders placeholder rows instead of the data. Keeps the table's height and
   * column widths steady while a page loads, so the layout doesn't jump and the
   * user isn't shown a bare "Loading…" line where the rows were.
   */
  loading?: boolean;
  /** Placeholder row count while `loading` (defaults to the current row count, min 3). */
  skeletonRows?: number;
  className?: string;
}

const CELL_PAD = { small: "px-3 py-2.5", middle: "px-3 py-3", large: "px-4 py-3.5" } as const;

export function Table<T>({
  columns,
  data,
  rowKey,
  empty = "No data",
  onRowClick,
  size = "small",
  loading = false,
  skeletonRows,
  className,
}: TableProps<T>) {
  const pad = CELL_PAD[size];
  // The chevron rail is an affordance column: it tells the reader the row leads
  // somewhere, and gives the row a right edge to end on.
  const rail = onRowClick != null;
  const colCount = columns.length + (rail ? 1 : 0);
  const skeletonCount = skeletonRows ?? Math.max(data.length, 3);

  return (
    <div className={cn("w-full overflow-x-auto", className)}>
      <table className="w-full caption-bottom border-collapse text-sm">
        <thead>
          <tr className="border-b border-border">
            {columns.map((c) => (
              <th
                key={c.key}
                style={c.width != null ? ({ width: c.width } as CSSProperties) : undefined}
                className={cn(
                  "text-xs font-semibold tracking-wide whitespace-nowrap text-muted-foreground uppercase",
                  pad,
                  c.numeric ? "text-right" : "text-left",
                  c.hideOnMobile && "hidden sm:table-cell",
                )}
              >
                {c.title}
              </th>
            ))}
            {rail && <th className="w-8" aria-hidden />}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: skeletonCount }, (_, i) => (
              <tr key={`skeleton-${i}`} className="border-b border-border/50 last:border-0">
                {columns.map((c) => (
                  <td key={c.key} className={cn(pad, c.hideOnMobile && "hidden sm:table-cell")}>
                    {/* Staggered widths so the placeholder reads as text, not as bars. */}
                    <span
                      className="block h-3.5 animate-pulse rounded bg-muted"
                      style={{ width: `${[70, 55, 85, 40, 60][i % 5]}%` }}
                    />
                  </td>
                ))}
                {rail && <td />}
              </tr>
            ))
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={colCount} className={cn("py-10 text-center text-muted-foreground", pad)}>
                {empty}
              </td>
            </tr>
          ) : (
            data.map((row) => {
              const activate = onRowClick
                ? {
                    onClick: () => onRowClick(row),
                    onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
                      if (e.target !== e.currentTarget) return;
                      if (e.key !== "Enter" && e.key !== " ") return;
                      e.preventDefault();
                      onRowClick(row);
                    },
                    tabIndex: 0,
                  }
                : {};
              return (
                <tr
                  key={rowKey(row)}
                  {...activate}
                  className={cn(
                    "group border-b border-border/50 transition-colors last:border-0",
                    onRowClick &&
                      "cursor-pointer hover:bg-accent focus-visible:bg-accent focus-visible:outline-none",
                  )}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn(
                        pad,
                        c.numeric && "text-right tabular-nums",
                        c.hideOnMobile && "hidden sm:table-cell",
                      )}
                    >
                      {c.render ? c.render(row) : ((row as Record<string, ReactNode>)[c.key] ?? null)}
                    </td>
                  ))}
                  {rail && (
                    <td className="pr-2 text-right">
                      <ChevronRight
                        className="inline size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                        aria-hidden
                      />
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
